import React, { Component } from 'react';
import { Nav, NavDropdown, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Octicon, {Person} from '@github/octicons-react';
import { useGoogleLogin, googleLogout } from '@react-oauth/google';
import { GoSignOut } from "react-icons/go";
import SessionCountdown from "components/controls/sessionCountdown";
import Config from "context/config";
import AccountTypes from "context/accountTypes";
import Utils from "context/utils";
import './user.css'

export default class UserMenu extends Component {
  componentDidMount() {
    this.hydrateIdentity();
  }

  // `app` arrives as one monolithic object (`app={this.state}` from App), so any unrelated slice of
  // App state re-renders this menu. Only re-hydrate when the identity-relevant part of it — the auth
  // object itself, or its user_id — actually changed, not on every update.
  componentDidUpdate(prevProps) {
    const prevAuth = prevProps.app && prevProps.app.auth;
    const auth = this.props.app && this.props.app.auth;
    const authChanged = prevAuth !== auth
      || (!!prevAuth && !!auth && prevAuth.user_id !== auth.user_id);
    if ( authChanged ) {
      this.hydrateIdentity();
    }
  }

  // The menu is the first thing that needs to know who you are, so it is where a session stored
  // before this feature — or one just created by signing in — gets its identity filled in.
  hydrateIdentity() {
    const app = this.props.app;
    if ( app.authenticated && app.auth && !app.auth.anonymous && !app.auth.user_id ) {
      app.api.ensureSessionIdentity();
    }
  }

  returnToParked = () => {
    this.props.app.api.stopActing(false);
  }

  responseGoogleSuccess = (response) => {
    // Check that response contains all the info we need
    if ( !response || !response.access_token ) {
      this.responseGoogleError({});
      return;
    }

    // Straight to the backend with the token. This used to fetch Google's userinfo first and hand the id,
    // name and email along to be trusted -- but an identity the client looks up is an identity a caller can
    // choose, so the backend reads all three from Google itself and no longer accepts them here.
    return this.props.app.api
      .authGoogleSigninPost(response.access_token)
      .catch((err) => {
        this.responseGoogleError(err);
      });
  }

  responseGoogleError = (error) => {
    // No op for now
  }

  signout = () => {
    // Sign out
    this.props.app.api.signout();

    // Navigate to home page
    // NEXT: This should be done more gracefully via router which is not currently accessible from here.
    window.location.assign("/");
  }

  render() {
    const LoginWrapper = ({shell, ...rest}) => {
      const login = useGoogleLogin({
        onSuccess: (response) => {
          shell.responseGoogleSuccess(response);
        },
        onError: (error) => {
          shell.responseGoogleError(error);
        }
      });

      return (
        <Button
          onClick={login}
          variant="outline-success"
          size="sm"
          className="btn-signin">
          Sign in
        </Button>
      )
    }

    const LogoutWrapper = ({shell, ...rest}) => {
      const logout = () => {
        // Cleanup library state
        googleLogout();

        // Cleanup app state
        shell.signout();
      };

      return (
        <NavDropdown.Item onClick={logout}>Sign out</NavDropdown.Item>
      )
    }

    const auth = this.props.app.auth;
    const acting = this.props.app.api.isActing();
    const parked = acting ? this.props.app.api.parkedSession() : null;
    const Glyph = AccountTypes.of(auth).glyph;
    // Same name-or-short-id fallback the acting header and the "Return to" item use. A session
    // parked — or, here, active — before its identity hydrated has neither, and "Logged in as "
    // with nothing after it names nothing at all, so the header is omitted rather than dangling.
    const loggedInAsName = auth && (auth.name || Utils.shortId(auth.user_id));

    const userDropDownTitle = (
      <span className="justify-content-end global-user-avatar">
        {AccountTypes.isHuman(auth)
          ? <Octicon icon={Person} size="medium"/>
          : <Glyph size={20}/>
        }
      </span>
    );

    return (
      <>
        <>
          {this.props.app.authenticated && this.props.app.auth && !this.props.app.auth.anonymous ? (
            <NavDropdown title={userDropDownTitle} id="basic-nav-dropdown" alignRight>
              {acting &&
                <>
                  <NavDropdown.Header>
                    Acting as <strong>{auth.name}</strong>
                    {/* Same live expiry countdown the acting pill shows, driven by the identical
                        shared component so there is exactly one timer/cleanup/zero-handling code
                        path — not the short id, which is still reachable via Settings → Profile. */}
                    <div className="text-muted">
                      <SessionCountdown expiresAt={auth.expiresAt} api={this.props.app.api}/>
                    </div>
                  </NavDropdown.Header>
                  {parked &&
                    <NavDropdown.Item onClick={this.returnToParked}>
                      {/* Same GoSignOut icon as the acting pill's exit control, and the same
                          "<Icon/> Label" spacing this codebase uses for icon-bearing buttons (e.g.
                          accounts.js's "Act as"), so the two controls that do the same thing look
                          like they do the same thing. A session parked before its identity was
                          hydrated has no name, and "Return to " with nothing after it is not a
                          control — same fallback the acting pill uses. */}
                      <GoSignOut/>&nbsp;&nbsp;Return to {parked.name || Utils.shortId(parked.user_id)}
                    </NavDropdown.Item>
                  }
                  <NavDropdown.Divider/>
                </>
              }
              {!acting && loggedInAsName &&
                <>
                  <NavDropdown.Header>
                    Logged in as <strong>{loggedInAsName}</strong>
                  </NavDropdown.Header>
                  <NavDropdown.Divider/>
                </>
              }
              <NavDropdown.Item as={Link} to="/p/me">Profile</NavDropdown.Item>
              <NavDropdown.Divider/>
              <LogoutWrapper shell={this}/>
            </NavDropdown>
          ) : (
            <LoginWrapper shell={this}/>
          )}
        </>
      </>
    )
  }
}
